import { useEffect, useMemo, useRef, useState } from "react";
import HandleNoData from "../../components/HandleNoData";
import EmptyState from "../../components/EmptyState";
import { TemplatesService } from "../../services/templatesService";
import Button from "../../components/Button";
import useRequest from "../../hooks/useRequest";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { parserStringToHTML } from "../../utils/templateWhatsapp";

declare const bootstrap: any;

function TemplatesPartial() {
  const { request, isPending } = useRequest();

  const defaultValues = {
    template: "",
    is_opt_in: false,
    button_text: "",
  };
  const {
    control,
    reset,
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues,
  });

  const modalRef = useRef<HTMLDivElement>(null);
  const [modal, setModal] = useState<any>(null);

  const [templates, setTemplates] = useState([]);
  const [templateSelected, setTemplateSelected] = useState<any>(null);

  const header = useMemo(
    () => templateSelected?.components?.find((c) => c.type === "HEADER"),
    [templateSelected]
  );
  const body = useMemo(
    () => templateSelected?.components?.find((c) => c.type === "BODY"),
    [templateSelected]
  );
  const footer = useMemo(
    () => templateSelected?.components?.find((c) => c.type === "FOOTER"),
    [templateSelected]
  );
  const buttons = useMemo(
    () => templateSelected?.components?.find((c) => c.type === "BUTTONS"),
    [templateSelected]
  );

  const formatCategory = (category) => {
    switch (category) {
      case "UTILITY":
        return "Servicios";
      case "MARKETING":
        return "Marketing";
      case "AUTHENTICATION":
        return "Autenticación";

      default:
        return category;
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case "APPROVED":
        return <span className="badge bg-soft-success text-dark">Activa</span>;
      // case 'IN_APPEAL':
      //     return <span className="badge bg-soft-dark text-dark">{status}</span>
      case "PENDING":
        return (
          <span className="badge bg-soft-warning text-dark">En revisión</span>
        );
      case "REJECTED":
        return (
          <span className="badge bg-soft-danger text-dark">Rechazada</span>
        );
      case "PENDING_DELETION":
        return (
          <span className="badge bg-soft-danger text-dark">
            Borrado pendiente
          </span>
        );
      case "DELETED":
        return <span className="badge bg-soft-dark text-dark">Borrado</span>;
      case "DISABLED":
        return (
          <span className="badge bg-soft-warning text-dark">Inactivo</span>
        );
      case "PAUSED":
        return <span className="badge bg-soft-warning text-dark">Pausada</span>;
      case "LIMIT_EXCEEDED":
        return (
          <span className="badge bg-soft-danger text-dark">
            Límite excedido
          </span>
        );

      default:
        return (
          <span className="badge bg-soft-warning text-dark">{status}</span>
        );
    }
  };

  const getTemplates = async () => {
    const response = await TemplatesService.getAll();
    setTemplates(response);
  };

  const showModal = (data) => {
    setTemplateSelected(data);
    modal.show();
  };

  const updateTemplates = async () => {
    try {
      await request(TemplatesService.update);
      getTemplates();
    } catch (error) {
      console.error(error);
      alert("No se actualizar las plantillas de WhatsApp");
    }
  };

  const save = async (data: typeof defaultValues) => {
    try {
      data.template = templateSelected._id;
      await TemplatesService.handleOptIn(data);
      await getTemplates();

      modal.hide();
    } catch (error) {
      console.error(error);
      alert("No se guardar la configuración");
    }
  };

  useEffect(() => {
    if (templateSelected) {
      reset({
        is_opt_in: templateSelected.is_opt_in,
        button_text: templateSelected.opt_in_text,
      })
    }
  }, [templateSelected]);

  useEffect(() => {
    if (modalRef.current) {
      const modalInstance = new bootstrap.Modal(modalRef.current, {});
      setModal(modalInstance);
    }
  }, [modalRef]);

  useEffect(() => {
    getTemplates();
  }, []);

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <h5>Plantillas de WhatsApp</h5>
        <Button
          type="submit"
          className="btn btn-outline-primary ms-auto"
          isLoading={isPending}
          onClick={updateTemplates}
        >
          Actualizar
        </Button>
      </div>

      <table className="card-table table">
        <thead>
          <tr>
            <th scope="col">Nombre de la plantilla </th>
            <th scope="col">Idioma </th>
            <th scope="col">Estado </th>
            <th scope="col"> </th>
          </tr>
        </thead>
        <tbody>
          <HandleNoData
            condition={templates.length}
            emptyComponent={
              <tr>
                <td colSpan={4}>
                  <EmptyState />
                </td>
              </tr>
            }
          >
            {templates.map((row: any) => (
              <tr key={row._id}>
                <td>
                  <p className="text-dark fw-semibold">{row.name}</p>
                  <p className="small">{formatCategory(row.category)}</p>
                  { row.is_opt_in &&  <span className="badge bg-soft-success text-dark">Plantilla Opt-In</span>}
                </td>
                <td>{row.language}</td>
                <td>{formatStatus(row.status)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-outline-edit btn-icon m-l-8"
                    onClick={() => showModal(row)}
                  >
                    <i className="bi bi-eye-fill"></i>
                  </button>
                </td>
              </tr>
            ))}
          </HandleNoData>
        </tbody>
      </table>

      <div
        className="modal fade"
        ref={modalRef}
        tabIndex={-1}
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Vista previa</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="row ">
                <div className="col">
                  <div className="template-preview">
                    <div className="template-preview-container mb-1">
                      {header && (
                        <div className="template-preview-header mb-2">
                          <p className="text-dark fw-semibold">{header.text}</p>
                        </div>
                      )}
                      <div className="template-preview-body">
                        <p dangerouslySetInnerHTML={{ __html: body?.text ? parserStringToHTML(body?.text): ''}}></p>
                      </div>

                      {footer && (
                        <div className="template-preview-footer mt-2">
                          {" "}
                          <p className="small">{footer.text}</p>{" "}
                        </div>
                      )}
                    </div>

                    {buttons?.buttons?.map((btn, i) => (
                      <div className="template-preview-btn" key={i}>
                        <p>{btn.text}</p>
                      </div>
                    ))}
                  </div>

                  {buttons?.buttons?.length > 0 && (
                    <div className="card">
                      <div className="card-body">
                        <div className="">
                          <div>
                            <p className="text-dark fw-bold">Opt-In</p>
                            <p>
                              Antes de enviar mensajes a las personas por
                              WhatsApp, las empresas deben obtener un permiso de
                              aceptación. Las empresas pueden contactar a las
                              personas por WhatsApp si: (a) dieron su número de
                              teléfono celular y (b) las empresas obtuvieron el
                              permiso de aceptación del destinatario para
                              recibir mensajes o llamadas posteriores de una
                              empresa en particular.
                            </p>
                          </div>

                          <div className="ms-auto nowrap mt-5">
                            <div className="form-check form-switch ">
                              <label
                                className="form-check-label"
                                htmlFor="is_opt_in"
                              >
                                ¿Usar esta plantilla como Opt-In?
                              </label>
                              <input
                                type="checkbox"
                                className="form-check-input form-check-info"
                                id="is_opt_in"
                                {...register("is_opt_in")}
                              />
                            </div>
                          </div>

                          <div className="mt-3">
                            <label className="form-check-label">
                              Seleccionar el botón para rechazar el opt-in *
                            </label>
                            <Controller
                              control={control}
                              name="button_text"
                              rules={{ required: true }}
                              render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                              }) => {
                                const options =
                                  buttons?.buttons?.map((btn) => ({
                                    value: btn.text,
                                    label: btn.text,
                                  })) || [];

                                return (
                                  <Select
                                    placeholder="Seleccionar el botón para rechazar el opt-in."
                                    options={options}
                                    className={
                                      "form-check-label" +
                                      (error
                                        ? "form-select-custom is-invalid"
                                        : "")
                                    }
                                    classNamePrefix="form-select-custom"
                                    onBlur={onBlur}
                                    onChange={(value: any) =>
                                      onChange(value?.value)
                                    }
                                    isClearable
                                    value={
                                      options.find(
                                        (a: any) => a.value === value
                                      ) || ""
                                    }
                                    isDisabled={!watch("is_opt_in")}
                                  />
                                );
                              }}
                            />
                            <span className="invalid-feedback">
                              Campo obligatorio.
                            </span>
                          </div>

                          <div className="mt-3">
                            <Button
                              type="submit"
                              className="btn btn-primary btn-sm"
                              isLoading={isSubmitting}
                              onClick={handleSubmit(save)}
                            >
                              Guardar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default TemplatesPartial;
